// src/modules/finan/hooks/useInstallmentHandlers.ts

import { useToast } from "../../../shared/hooks";
import { ToastContent } from "../../../shared/components";
import { FinancialService } from "../../../shared/services/api/financial/FinancialService";
import { IInstallmentTable } from "../types/types";
import { ProposalService } from "../../../shared/services/api/proposal/ProposalService";

export const useInstallmentHandlers = (dataIni: string, dataFim: string, setIsLoading: (loading: boolean) => void) => {
	const toastHandler = useToast();

	const handleExportPdf = async (row: IInstallmentTable) => {
		const IID = row.clientId;
		setIsLoading(true);

		try {
			const result = await FinancialService.getInstallmentListPDF(IID, dataIni, dataFim, "credit_card");

			if (result instanceof Error) {
				toastHandler.present({
					type: "error",
					position: "top-right",
					messageOrContent: <ToastContent title="Erro ao exportar PDF" content="Tente novamente mais tarde." />,
				});
				return;
			}
		} catch (error) {
			console.error("Erro inesperado:", error);
			toastHandler.present({
				type: "error",
				position: "top-right",
				messageOrContent: <ToastContent title="Erro inesperado" content="Ocorreu um erro ao gerar o PDF." />,
			});
		} finally {
			setIsLoading(false);
		}
	};

	const handleCancelInstallments = async (obs: string, ids: string[]) => {
		if (!ids.length) return;

		setIsLoading(true);
		let successCount = 0;

		try {
			for (const id of ids) {
				const result = await ProposalService.saveCancelInstallment({ obs }, id);
				if (!(result instanceof Error)) {
					successCount++;
				}
			}

			if (successCount > 0) {
				toastHandler.present({
					type: "success",
					position: "top-right",
					messageOrContent: (
						<ToastContent
							title="Sucesso"
							content={successCount === 1 ? "Parcela cancelada com sucesso." : `${successCount} parcelas canceladas com sucesso.`}
						/>
					),
				});
			} else {
				toastHandler.present({
					type: "error",
					position: "top-right",
					messageOrContent: <ToastContent title="Erro" content="Não foi possível cancelar as parcelas." />,
				});
			}
		} catch (error) {
			console.error("Erro inesperado:", error);
			toastHandler.present({
				type: "error",
				position: "top-right",
				messageOrContent: <ToastContent title="Erro" content="Falha ao processar os cancelamentos." />,
			});
		} finally {
			setIsLoading(false);
		}
	};

	const handlePaymentInstallments = async (obs: string, ids: string[]) => {
		if (!ids.length) return;

		setIsLoading(true);
		let successCount = 0;

		try {
			for (const id of ids) {
				const result = await ProposalService.savePaymentInstallment({ obs }, id); // novo endpoint
				if (!(result instanceof Error)) {
					successCount++;
				}
			}

			if (successCount > 0) {
				toastHandler.present({
					type: "success",
					position: "top-right",
					messageOrContent: (
						<ToastContent
							title="Sucesso"
							content={successCount === 1 ? "Cobrança baixada com sucesso." : `${successCount} cobranças baixadas com sucesso.`}
						/>
					),
				});
			} else {
				toastHandler.present({
					type: "error",
					position: "top-right",
					messageOrContent: <ToastContent title="Erro" content="Não foi possível realizar a baixa manual." />,
				});
			}
		} catch (error) {
			console.error("Erro inesperado:", error);
			toastHandler.present({
				type: "error",
				position: "top-right",
				messageOrContent: <ToastContent title="Erro" content="Falha ao processar as baixas manuais." />,
			});
		} finally {
			setIsLoading(false);
		}
	};

	const handleSwitchInstallments = async (ids: string[], status: "billet" | "credit_card") => {
		if (!ids.length) return;
		setIsLoading(true);
		let successCount = 0;

		try {
			for (const id of ids) {
				const result = await ProposalService.updateSwitchPayment(id, { status }); // id primeiro, depois dados
				if (!(result instanceof Error)) {
					successCount++;
				}
			}

			if (successCount > 0) {
				toastHandler.present({
					type: "success",
					position: "top-right",
					messageOrContent: (
						<ToastContent
							title="Sucesso"
							content={successCount === 1 ? "Conversão realizada com sucesso." : `${successCount} conversões realizadas com sucesso.`}
						/>
					),
				});
			} else {
				toastHandler.present({
					type: "error",
					position: "top-right",
					messageOrContent: <ToastContent title="Erro" content="Não foi possível realizar a conversão." />,
				});
			}
		} catch (error) {
			console.error("Erro inesperado:", error);
			toastHandler.present({
				type: "error",
				position: "top-right",
				messageOrContent: <ToastContent title="Erro" content="Falha ao processar a conversão" />,
			});
		} finally {
			setIsLoading(false);
		}
	};

	return {
		handleExportPdf,
		handleCancelInstallments,
		handlePaymentInstallments,
		handleSwitchInstallments,
	};
};
